/**
 * Extract threshold values from a config object
 */
export const getThresholdFromConfig = (config: any, metric: string, defaultValue: number): number => {
  if (!config) return defaultValue;
  const value = parseInt(config[metric] || defaultValue, 10);
  return isNaN(value) ? defaultValue : value;
};

/**
 * Get active readiness level value from condition
 */
export const getReadinessLevelValue = (condition: any, readinessLevelId: string): number => {
  if (!condition || !readinessLevelId) return 0;
  const value = parseInt((condition as any)[readinessLevelId] || '0', 10);
  return isNaN(value) ? 0 : value;
};

/**
 * Extract Purchek thresholds from config (supports both old and new format)
 */
export const getPurchekThresholdsForQuery = (purchekConfig?: any) => {
  // New format with conditions array
  if (purchekConfig && purchekConfig.conditions && Array.isArray(purchekConfig.conditions)) {
    const readinessLevel = purchekConfig.activeReadiness?.[0] || 'high';
    const neighborhoodCondition = purchekConfig.conditions.find((c: any) => c.id === 'purchek_neighborhood_sites');
    const penetrationCondition = purchekConfig.conditions.find((c: any) => c.id === 'purchek_penetration');
    const crimeCondition = purchekConfig.conditions.find((c: any) => c.id === 'purchek_crime_incidents');

    return {
      neighborhoodSites: getReadinessLevelValue(neighborhoodCondition, readinessLevel),
      penetration: getReadinessLevelValue(penetrationCondition, readinessLevel),
      crimeIncidents: getReadinessLevelValue(crimeCondition, readinessLevel)
    };
  }

  // Fallback to old format
  return {
    neighborhoodSites: getThresholdFromConfig(purchekConfig, 'Number of Neighbourhood Purchek Sites', 5),
    penetration: getThresholdFromConfig(purchekConfig, 'Purchek Penetration Percentage', 75),
    crimeIncidents: getThresholdFromConfig(purchekConfig, 'Number of Crime Incidents', 5000)
  };
};

/**
 * Extract Containment thresholds from config (supports both old and new format)
 */
export const getContainmentThresholdsForQuery = (containmentConfig?: any) => {
  // New format with conditions array
  if (containmentConfig && containmentConfig.conditions && Array.isArray(containmentConfig.conditions)) {
    const readinessLevel = containmentConfig.activeReadiness?.[0] || 'high';
    const neighborhoodCondition = containmentConfig.conditions.find((c: any) => c.id === 'containment_neighborhood_sites');
    const penetrationCondition = containmentConfig.conditions.find((c: any) => c.id === 'containment_penetration');
    const crimeCondition = containmentConfig.conditions.find((c: any) => c.id === 'containment_crime_incidents');
    const plazaCondition = containmentConfig.conditions.find((c: any) => c.id === 'containment_plaza_sites');
    const ordinanceCondition = containmentConfig.conditions.find((c: any) => c.id === 'containment_ordinance');
    const minimumCartsCondition = containmentConfig.conditions.find((c: any) => c.id === 'containment_minimum_carts');

    return {
      neighborhoodSites: getReadinessLevelValue(neighborhoodCondition, readinessLevel),
      penetration: getReadinessLevelValue(penetrationCondition, readinessLevel),
      crimeIncidents: getReadinessLevelValue(crimeCondition, readinessLevel),
      plazaSites: getReadinessLevelValue(plazaCondition, readinessLevel),
      ordinance: getReadinessLevelValue(ordinanceCondition, readinessLevel),
      minimumCarts: getReadinessLevelValue(minimumCartsCondition, readinessLevel)
    };
  }

  // Fallback to old format
  return {
    neighborhoodSites: getThresholdFromConfig(containmentConfig, 'Number of Neighbourhood Containment Sites', 5),
    penetration: getThresholdFromConfig(containmentConfig, 'Containment Penetration Percentage', 75),
    crimeIncidents: getThresholdFromConfig(containmentConfig, 'Number of Crime Incidents', 5000),
    plazaSites: getThresholdFromConfig(containmentConfig, 'Part of a plaza', 0),
    ordinance: getThresholdFromConfig(containmentConfig, 'Ordinance', 0),
    minimumCarts: getThresholdFromConfig(containmentConfig, 'Should have at minimum carts', 100)
  };
};

/**
 * Extract Cart Manager thresholds from config (supports both old and new format)
 */
export const getCartManagerThresholdsForQuery = (cartManagerConfig?: any) => {
  // New format with conditions array
  if (cartManagerConfig && cartManagerConfig.conditions && Array.isArray(cartManagerConfig.conditions)) {
    const readinessLevel = cartManagerConfig.activeReadiness?.[0] || 'high';
    const cartsCondition = cartManagerConfig.conditions.find((c: any) => c.id === 'cart_manager_estimated_carts');
    const hotDaysCondition = cartManagerConfig.conditions.find((c: any) => c.id === 'cart_manager_hot_days');
    const coldDaysCondition = cartManagerConfig.conditions.find((c: any) => c.id === 'cart_manager_cold_days');

    return {
      estimatedCarts: getReadinessLevelValue(cartsCondition, readinessLevel),
      hotDays: getReadinessLevelValue(hotDaysCondition, readinessLevel),
      coldDays: getReadinessLevelValue(coldDaysCondition, readinessLevel)
    };
  }

  // Fallback to old format
  return {
    estimatedCarts: getThresholdFromConfig(cartManagerConfig, 'Estimated Number of Carts', 300),
    hotDays: getThresholdFromConfig(cartManagerConfig, 'Number of severely hot days', 90),
    coldDays: getThresholdFromConfig(cartManagerConfig, 'Number of severely cold days', 90)
  };
};

/**
 * Extract Subscription thresholds from config (supports both old and new format)
 */
export const getSubscriptionThresholdsForQuery = (subscriptionConfig?: any) => {
  // New format with conditions array
  if (subscriptionConfig && subscriptionConfig.conditions && Array.isArray(subscriptionConfig.conditions)) {
    const readinessLevel = subscriptionConfig.activeReadiness?.[0] || 'high';
    const purchekCondition = subscriptionConfig.conditions.find((c: any) => c.id === 'subscription_has_purchek');

    return {
      requiresPurchek: getReadinessLevelValue(purchekCondition, readinessLevel) > 0
    };
  }

  // Fallback to old format
  return {
    requiresPurchek: true
  };
};

/**
 * Extract Paired Wheel thresholds from config (supports both old and new format)
 */
export const getPairedWheelThresholdsForQuery = (pairedWheelConfig?: any) => {
  // New format with conditions array
  if (pairedWheelConfig && pairedWheelConfig.conditions && Array.isArray(pairedWheelConfig.conditions)) {
    const readinessLevel = pairedWheelConfig.activeReadiness?.[0] || 'high';
    const penetrationCondition = pairedWheelConfig.conditions.find((c: any) => c.id === 'paired_wheel_penetration');
    const replacementRateCondition = pairedWheelConfig.conditions.find((c: any) => c.id === 'paired_wheel_replacement_rate');
    const serviceFrequencyCondition = pairedWheelConfig.conditions.find((c: any) => c.id === 'paired_wheel_service_frequency');

    return {
      penetration: getReadinessLevelValue(penetrationCondition, readinessLevel),
      replacementRate: getReadinessLevelValue(replacementRateCondition, readinessLevel),
      serviceFrequency: getReadinessLevelValue(serviceFrequencyCondition, readinessLevel)
    };
  }

  // Fallback to old format
  return {
    penetration: 75,
    replacementRate: 4,
    serviceFrequency: 50
  };
};

export const calculateStoreRecommendations = (store: any, brandEstimatedCarts: number = 0, purchekConfig?: any, cartManagerConfig?: any, containmentConfig?: any, subscriptionConfig?: any, pairedWheelConfig?: any, purchekActiveReadinessName?: string, cartManagerActiveReadinessName?: string, containmentActiveReadinessName?: string, subscriptionActiveReadinessName?: string, pairedWheelActiveReadinessName?: string) => {
  const recommendations = {
    purchek: [] as string[],
    containment: [] as string[],
    cartManager: [] as string[],
    subscription: [] as string[],
    pairedWheel: [] as string[],
    purchekBadge: { matches: false, tooltip: '' },
    cartManagerBadge: { matches: false, tooltip: '' },
    containmentBadge: { matches: false, tooltip: '' },
    subscriptionBadge: { matches: false, tooltip: '' },
    pairedWheelBadge: { matches: false, tooltip: '' }
  };

  if (!store.purchek_install_date) {
    const { purchek_customer_count, penetration_purchek } = store.nearby_penetration || {};
    const { total_crime_incidents } = store.geo || {};

    // Default thresholds
    let neighborhoodSitesThreshold = 5;
    let penetrationThreshold = 75;
    let crimeThreshold = 5000;

    // Use config thresholds if provided
    if (purchekConfig) {
      neighborhoodSitesThreshold = parseInt(purchekConfig['Number of Neighbourhood Purchek Sites'] || '5', 10);
      penetrationThreshold = parseInt(purchekConfig['Purchek Penetration Percentage'] || '75', 10);
      crimeThreshold = parseInt(purchekConfig['Number of Crime Incidents'] || '5000', 10);
    }

    // Check conditions for recommendations
    if (purchek_customer_count >= neighborhoodSitesThreshold) {
      recommendations.purchek.push(`${purchek_customer_count} Purchek stores in the neighbourhood within 5 miles.`);
    }
    if (penetration_purchek >= penetrationThreshold) {
      const formattedPenetration = parseFloat(penetration_purchek).toFixed(2);
      recommendations.purchek.push(`High Purchek market penetration with ${formattedPenetration}% in neighbourhood.`);
    }
    if (total_crime_incidents >= crimeThreshold) {
      recommendations.purchek.push(`High crime neighbourhood with ${total_crime_incidents} crime incidents per year.`);
    }

    // Check badge conditions - show badge only if any condition is met
    if (recommendations.purchek.length > 0) {
      const condition1Met = purchek_customer_count >= neighborhoodSitesThreshold;
      const condition2Met = penetration_purchek >= penetrationThreshold;
      const condition3Met = total_crime_incidents >= crimeThreshold;

      recommendations.purchekBadge.matches = true;
      const readinessName = purchekActiveReadinessName || 'High';
      recommendations.purchekBadge.tooltip = `Active Config (${readinessName}):\nNeighbourhood Sites: ${purchek_customer_count} / ${neighborhoodSitesThreshold} ${condition1Met ? '✓' : '✗'}\nPenetration: ${penetration_purchek}% / ${penetrationThreshold}% ${condition2Met ? '✓' : '✗'}\nCrime Incidents: ${total_crime_incidents} / ${crimeThreshold} ${condition3Met ? '✓' : '✗'}\n\nMatching Conditions: ${[condition1Met && 'Neighbourhood Sites', condition2Met && 'Penetration', condition3Met && 'Crime Incidents'].filter(Boolean).join(', ')}`;
    }
  }

  if (!store.containment_install_date) {
    const { containment_customer_count, penetration_containment, plaza_cm_customer_count } = store.nearby_penetration || {};
    const { total_crime_incidents, penalty_for_cart_owner } = store.geo || {};

    // Default thresholds
    let neighborhoodSitesThreshold = 5;
    let penetrationThreshold = 75;
    let crimeThreshold = 5000;
    let plazaSitesThreshold = 0;
    let ordinanceThreshold = 0;

    // Use config thresholds if provided
    if (containmentConfig) {
      neighborhoodSitesThreshold = parseInt(containmentConfig['Number of Neighbourhood Containment Sites'] || '5', 10);
      penetrationThreshold = parseInt(containmentConfig['Containment Penetration Percentage'] || '75', 10);
      crimeThreshold = parseInt(containmentConfig['Number of Crime Incidents'] || '5000', 10);
      plazaSitesThreshold = parseInt(containmentConfig['Part of a plaza'] || '0', 10);
      ordinanceThreshold = parseInt(containmentConfig['Ordinance'] || '0', 10);
    }

    // Check conditions for recommendations
    if (containment_customer_count >= neighborhoodSitesThreshold) {
      recommendations.containment.push(`${containment_customer_count} containment stores in the neighbourhood within 5 miles.`);
    }
    if (penetration_containment >= penetrationThreshold) {
      const formattedPenetration = parseFloat(penetration_containment).toFixed(2);
      recommendations.containment.push(`High containment market penetration with ${formattedPenetration}% in neighbourhood.`);
    }
    if (total_crime_incidents >= crimeThreshold) {
      recommendations.containment.push(`High crime neighbourhood with ${total_crime_incidents} crime incidents per year.`);
    }
    if (plazaSitesThreshold > 0 && plaza_cm_customer_count > 0) {
      recommendations.containment.push(`${plaza_cm_customer_count} containment stores in shopping complex within 0.5 miles.`);
    }
    if (ordinanceThreshold > 0 && penalty_for_cart_owner === true) {
      const { law_summary } = store.geo || {};
      recommendations.containment.push(law_summary || 'Legal cart owner penalty enforcement identified.');
    }

    // Check badge conditions - show badge only if any condition is met
    if (recommendations.containment.length > 0) {
      const condition1Met = containment_customer_count >= neighborhoodSitesThreshold;
      const condition2Met = penetration_containment >= penetrationThreshold;
      const condition3Met = total_crime_incidents >= crimeThreshold;
      const condition4Met = plazaSitesThreshold > 0 && plaza_cm_customer_count > 0;
      const condition5Met = ordinanceThreshold > 0 && penalty_for_cart_owner === true;

      recommendations.containmentBadge.matches = true;
      const readinessName = containmentActiveReadinessName || 'High';
      const ordinanceConfigValue = ordinanceThreshold > 0 ? 'Yes' : 'No';
      const ordinanceStoreValue = penalty_for_cart_owner === true ? 'Yes' : 'No';
      recommendations.containmentBadge.tooltip = `Active Config (${readinessName}):\nNeighbourhood Sites: ${containment_customer_count} / ${neighborhoodSitesThreshold} ${condition1Met ? '✓' : '✗'}\nPenetration: ${penetration_containment}% / ${penetrationThreshold}% ${condition2Met ? '✓' : '✗'}\nCrime Incidents: ${total_crime_incidents} / ${crimeThreshold} ${condition3Met ? '✓' : '✗'}\nPart of Plaza: ${plaza_cm_customer_count} / ${plazaSitesThreshold} ${condition4Met ? '✓' : '✗'}\nOrdinance: ${ordinanceStoreValue} / ${ordinanceConfigValue} ${condition5Met ? '✓' : '✗'}\n\nMatching Conditions: ${[condition1Met && 'Neighbourhood Sites', condition2Met && 'Penetration', condition3Met && 'Crime Incidents', condition4Met && 'Plaza Sites', condition5Met && 'Ordinance'].filter(Boolean).join(', ')}`;
    }
  }

  if (!store.cart_manager_install_date) {
    const { no_of_cold_days, no_of_hot_days } = store.geo || {};

    // Default thresholds
    let estimatedCartsThreshold = 300;
    let hotDaysThreshold = 90;
    let coldDaysThreshold = 90;

    // Use config thresholds if provided
    if (cartManagerConfig) {
      estimatedCartsThreshold = parseInt(cartManagerConfig['Estimated Number of Carts'] || '300', 10);
      hotDaysThreshold = parseInt(cartManagerConfig['Number of severely hot days'] || '90', 10);
      coldDaysThreshold = parseInt(cartManagerConfig['Number of severely cold days'] || '90', 10);
    }

    // Check conditions for recommendations
    if (brandEstimatedCarts >= estimatedCartsThreshold) {
      recommendations.cartManager.push(`Estimated inventory of ${brandEstimatedCarts} carts per store.`);
    }
    if (no_of_cold_days >= coldDaysThreshold) {
      recommendations.cartManager.push(`${no_of_cold_days} days of extreme cold weather per year.`);
    }
    if (no_of_hot_days >= hotDaysThreshold) {
      recommendations.cartManager.push(`${no_of_hot_days} days of extreme hot weather per year.`);
    }

    // Check badge conditions - show badge only if any condition is met
    if (recommendations.cartManager.length > 0) {
      const condition1Met = brandEstimatedCarts >= estimatedCartsThreshold;
      const condition2Met = no_of_hot_days >= hotDaysThreshold;
      const condition3Met = no_of_cold_days >= coldDaysThreshold;

      recommendations.cartManagerBadge.matches = true;
      const readinessName = cartManagerActiveReadinessName || 'High';
      recommendations.cartManagerBadge.tooltip = `Active Config (${readinessName}):\nEstimated Carts: ${brandEstimatedCarts} / ${estimatedCartsThreshold} ${condition1Met ? '✓' : '✗'}\nHot Days: ${no_of_hot_days} / ${hotDaysThreshold} ${condition2Met ? '✓' : '✗'}\nCold Days: ${no_of_cold_days} / ${coldDaysThreshold} ${condition3Met ? '✓' : '✗'}\n\nMatching Conditions: ${[condition1Met && 'Estimated Carts', condition2Met && 'Hot Days', condition3Met && 'Cold Days'].filter(Boolean).join(', ')}`;
    }
  }

  // Subscription recommendation - only show if store has Purchek installed
  if (!store.subscription_start_date && subscriptionConfig) {
    const subscriptionThresholds = getSubscriptionThresholdsForQuery(subscriptionConfig);

    // Pre-condition: Must have Purchek
    if (subscriptionThresholds.requiresPurchek && store.purchek_install_date) {
      recommendations.subscription.push('Store has Purchek installed - eligible for Subscription.');

      recommendations.subscriptionBadge.matches = true;
      const readinessName = subscriptionActiveReadinessName || 'High';
      recommendations.subscriptionBadge.tooltip = `Active Config (${readinessName}):\nPre-condition: Must have Purchek ✓`;
    }
  }

  // Paired Wheel recommendation - only show if store doesn't have Paired Wheels installed
  if (!store.paired_wheel_install_date && pairedWheelConfig) {
    const { penetration_paired_wheel } = store.nearby_penetration || {};
    const { single_wheel_replacement_rate, service_frequency } = store.wheel || {};

    const pairedWheelThresholds = getPairedWheelThresholdsForQuery(pairedWheelConfig);
    const penetrationThreshold = pairedWheelThresholds.penetration;
    const replacementRateThreshold = pairedWheelThresholds.replacementRate;
    const serviceFrequencyThreshold = pairedWheelThresholds.serviceFrequency;

    // Check conditions for recommendations
    const penetrationMet = penetration_paired_wheel >= penetrationThreshold;
    const replacementRateMet = single_wheel_replacement_rate >= replacementRateThreshold;
    const serviceFrequencyMet = service_frequency >= serviceFrequencyThreshold;

    const anyConditionMet = penetrationMet || replacementRateMet || serviceFrequencyMet;

    if (anyConditionMet) {
      const recommendations_list = [];
      if (penetrationMet) {
        const formattedPenetration = parseFloat(penetration_paired_wheel).toFixed(2);
        recommendations_list.push(`High Paired Wheel market penetration with ${formattedPenetration}% in neighbourhood.`);
      }
      if (replacementRateMet) {
        recommendations_list.push(`Single wheels replacement rate of ${single_wheel_replacement_rate} meets threshold of ${replacementRateThreshold}.`);
      }
      if (serviceFrequencyMet) {
        recommendations_list.push(`Service frequency of ${service_frequency} meets threshold of ${serviceFrequencyThreshold}.`);
      }

      recommendations.pairedWheel.push(...recommendations_list);

      // Set badge
      recommendations.pairedWheelBadge.matches = true;
      const readinessName = pairedWheelActiveReadinessName || 'High';
      const conditionDetails = `Penetration: ${penetration_paired_wheel || 'N/A'}% / ${penetrationThreshold}% ${penetrationMet ? '✓' : '✗'}\nReplacement Rate: ${single_wheel_replacement_rate || 'N/A'} / ${replacementRateThreshold} ${replacementRateMet ? '✓' : '✗'}\nService Frequency: ${service_frequency || 'N/A'} / ${serviceFrequencyThreshold} ${serviceFrequencyMet ? '✓' : '✗'}`;
      const matchingConditions = [penetrationMet && 'Penetration', replacementRateMet && 'Replacement Rate', serviceFrequencyMet && 'Service Frequency'].filter(Boolean).join(', ');
      recommendations.pairedWheelBadge.tooltip = `Active Config (${readinessName}):\n${conditionDetails}\n\nMatching Conditions: ${matchingConditions}`;
    }
  }

  return recommendations;
};
